import { useState, useEffect } from 'react';
import { Pie, Line,Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { ShoppingBag, Percent, Box } from 'lucide-react';
import { TbCategory } from "react-icons/tb";
import { MdOutlineInventory2 } from "react-icons/md";
import { MdOutlineDiscount } from "react-icons/md";
import { TbChartInfographic } from "react-icons/tb";
import { MdOutlineLocalShipping } from "react-icons/md";
import { SiNanostores } from "react-icons/si";
import AdminHome from './AdminHome'

const OrderView = () => {
  const [orders, setOrders] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(["shipped"]);



  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersResponse] = await Promise.all([
          fetch('/api/orders')
        ]);
        
        const ordersData = await ordersResponse.json();
        
        setOrders(ordersData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);


  // Filter logic remains the same
  const filterOrders = () => {
    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt || order.updatedAt);
      const isWithinDateRange =
        (!fromDate || new Date(fromDate) <= orderDate) &&
        (!toDate || new Date(toDate) >= orderDate);
      const isWithinYear =
        !selectedYear || orderDate.getFullYear().toString() === selectedYear;
      const matchesStatus = selectedStatuses.includes(order.deliveryStatus);
      return isWithinDateRange && isWithinYear && matchesStatus;
    });
  };

  const handleCheckboxChange = (status) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const filteredData = selectedStatuses.map((status) => {
    const count = filterOrders().filter((order) => order.deliveryStatus === status).length;
    return { status, count };
  });

  // Enhanced graph data with different colors for each status
  const graphData = {
    labels: filteredData.map((item) => item.status),
    datasets: [{
      label: "Orders by Status",
      data: filteredData.map((item) => item.count),
      backgroundColor: [
        '#4CAF50', // green for shipped
        '#FFA726', // orange for pending
        '#2196F3', // blue for delivered
        '#F44336'  // red for cancelled
      ],
      borderWidth: 0,
      borderRadius: 6,
      barThickness: 32,
    }]
  };

  const containerStyle = {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f8fafc'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0'
  };

  const inputStyles = {
    padding: '0.625rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    outline: 'none',
    width: '100%',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontSize: '0.875rem',
    ':focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    }
  };

  const checkboxStyle = {
    width: '1.125rem',
    height: '1.125rem',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem',
    accentColor: '#3b82f6'
  };




  return (
    <div>
        <div style={containerStyle}>
        <div style={cardStyle}>
          {/* Header Section */}
          <div style={{ borderBottom: '1px solid #e2e8f0',display:"flex",justifyContent:"space-between" }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#1a1a1a',
            }}><TbChartInfographic size={40} color='blue'/> Order Statistics</h2>
            
            <p style={{
              fontSize: '1rem',
              color: '#4a5568',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Total Orders: <span style={{ fontWeight: '600', color: 'green' }}>11</span>
            </p>
          </div>
  
          {/* Status Filter Section */}
          <div >
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#2d3748'
            }}>Filter by Delivery Status</h3>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.25rem',
              alignItems: 'center',
              backgroundColor: '#f8fafc',
              borderRadius: '8px'
            }}>
              {["shipped", "pending", "delivered", "cancelled"].map((status) => (
                <label key={status} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  backgroundColor: selectedStatuses.includes(status) ? '#eef2ff' : 'transparent',
                  transition: 'background-color 0.2s'
                }}>
                  <input
                    type="checkbox"
                    value={status}
                    checked={selectedStatuses.includes(status)}
                    onChange={() => handleCheckboxChange(status)}
                    style={checkboxStyle}
                  />
                  <span style={{
                    textTransform: 'capitalize',
                    color: '#4a5568',
                    fontWeight: selectedStatuses.includes(status) ? '500' : '400'
                  }}>{status}</span>
                </label>
              ))}
            </div>
          </div>
  
          {/* Date Range and Year Filter Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#2d3748'
            }}>Filter by Date Range & Year</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              backgroundColor: '#f8fafc',
              padding: '1.5rem',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', color: '#4a5568', fontWeight: '500' }}>From:</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={inputStyles}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', color: '#4a5568', fontWeight: '500' }}>To:</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={inputStyles}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', color: '#4a5568', fontWeight: '500' }}>Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ ...inputStyles, minWidth: '120px' }}
                >
                  <option value="">Select Year</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              </div>
            </div>
          </div>
  
          {/* Chart Section */}
          <div style={{ 
            height: '400px',
            backgroundColor: '#f8fafc',
            padding: '1.5rem',
            borderRadius: '8px'
          }}>
            {selectedStatuses.length > 0 ? (
              <Bar
                data={graphData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "top" },
                    title: { 
                      display: true, 
                      text: "Orders by Delivery Status",
                      font: { size: 16, weight: '600' },
                      padding: { bottom: 20 }
                    }
                  },
                  scales: {
                    y: { 
                      beginAtZero: true,
                      grid: { display: false },
                      border: { display: false },
                      ticks: { padding: 10 }
                    },
                    x: {
                      grid: { display: false },
                      border: { display: false },
                      ticks: { padding: 10 }
                    }
                  }
                }}
              />
            ) : (
              <p style={{
                textAlign: 'center',
                color: '#718096',
                marginTop: '2rem',
                fontSize: '0.875rem'
              }}>
                Please select at least one status to see the graph
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderView